import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeToDDLCollections,
  getCollectionDepthTag,
} from '../utils/storage';
import { getPermissions } from '../utils/permissions';
import AccessDeniedModal from './AccessDeniedModal';
import CreateCategoryModal from './CreateCategoryModal';
import AssignDDLModal from './AssignDDLModal';
import RegisterDropdownsPage from './RegisterDropdownsPage';
import { SearchBar, EmptyState } from './templates';
import './DDLCollection.css';

function ScrollTitle({ text }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [scrollDist, setScrollDist] = useState(0);

  const measure = useCallback(() => {
    if (outerRef.current && innerRef.current) {
      const overflow = innerRef.current.scrollWidth - outerRef.current.clientWidth;
      setScrollDist(overflow > 0 ? -overflow - 8 : 0);
    }
  }, []);

  useEffect(() => { measure(); }, [text, measure]);

  return (
    <h3 className="ddl-card-title" ref={outerRef}>
      <span
        className="ddl-card-title-inner"
        ref={innerRef}
        style={scrollDist < 0 ? { '--scroll-distance': `${scrollDist}px` } : undefined}
      >
        {text}
      </span>
    </h3>
  );
}

export default function DDLCollection() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDDLDenied, setShowDDLDenied] = useState(false);
  const [assignCol, setAssignCol] = useState(null);
  const [showRegister, setShowRegister] = useState(false);

  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const perms = getPermissions(userProfile?.roles || []);

  useEffect(() => {
    const unsubscribe = subscribeToDDLCollections((list) => {
      setCollections(list);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const filteredCollections = search.trim()
    ? collections.filter((c) =>
        c.name && c.name.toLowerCase().includes(search.trim().toLowerCase())
      )
    : collections;

  const sortedCollections = [...filteredCollections].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  if (loading) {
    return (
      <div className="page-container">
        <div className="empty-state"><p>Loading...</p></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="toolbar">
        {collections.length > 0 && (
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search collections..."
          />
        )}
        <div className="ddl-toolbar-btns">
          <button className="btn btn-secondary" onClick={() => setShowRegister(true)}>
            Register Dropdowns
          </button>
          <button
            className="btn btn-primary"
            onClick={() => perms.canManageDDL ? setShowCreateModal(true) : setShowDDLDenied(true)}
          >
            + New Collection
          </button>
        </div>
      </div>

      {collections.length === 0 ? (
        <EmptyState
          icon="📋"
          title="No collections yet"
          description="Create your first dropdown list collection to get started."
        >
          <button
            className="btn btn-primary"
            onClick={() => perms.canManageDDL ? setShowCreateModal(true) : setShowDDLDenied(true)}
          >
            + New Collection
          </button>
        </EmptyState>
      ) : sortedCollections.length === 0 ? (
        <EmptyState>
          <p className="ddl-no-results">No collections match your search.</p>
        </EmptyState>
      ) : (
        <div className="ddl-grid">
          {sortedCollections.map((col) => {
            const items = col.items || [];
            const itemCount = items.length;
            const depthTag = getCollectionDepthTag(items);
            const isAssigned = Array.isArray(col.assignedTo)
              ? col.assignedTo.length > 0
              : !!col.assignedTo;

            return (
              <div
                key={col.id}
                className="ddl-card"
                onClick={() => navigate(`/ddl-collection/${col.id}`)}
              >
                <button
                  className={`ddl-status-circle${isAssigned ? ' ddl-status-assigned' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAssignCol(col);
                  }}
                  title={isAssigned ? 'Assigned — click to manage' : 'Not assigned — click to assign'}
                >
                  {isAssigned && (
                    <svg viewBox="0 0 16 16" className="ddl-status-tick">
                      <polyline points="3.5 8.5 6.5 11.5 12.5 4.5" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>

                <ScrollTitle text={col.name} />
                <div className="ddl-card-meta">
                  <span className={`ddl-depth-tag ddl-depth-${depthTag.toLowerCase()}`}>
                    {depthTag}
                  </span>
                  <span className="ddl-card-count">
                    {itemCount} {itemCount === 1 ? 'item' : 'items'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <CreateCategoryModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => setShowCreateModal(false)}
        />
      )}

      {assignCol && (
        <AssignDDLModal
          collection={assignCol}
          onClose={() => setAssignCol(null)}
        />
      )}

      {showRegister && (
        <RegisterDropdownsPage
          collections={collections}
          onClose={() => setShowRegister(false)}
        />
      )}

      {showDDLDenied && (
        <AccessDeniedModal
          roles={userProfile?.roles || []}
          onClose={() => setShowDDLDenied(false)}
        />
      )}
    </div>
  );
}
